import { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle, DollarSign, TrendingUp, Store, Package } from "lucide-react";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const PRESETS = [
  { id: "today", label: "Today", days: 0 },
  { id: "7d", label: "7 days", days: 6 },
  { id: "30d", label: "30 days", days: 29 },
  { id: "90d", label: "90 days", days: 89 },
  { id: "365d", label: "Year", days: 364 },
];

function Stat({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
      <div className="ipv-eyebrow">{label}</div>
      <div className="font-display font-black text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminReports() {
  const [preset, setPreset] = useState("30d");
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState([]);
  const [selected, setSelected] = useState({});
  const [palletCost, setPalletCost] = useState("");
  const [costEdits, setCostEdits] = useState({});
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    const p = PRESETS.find((x) => x.id === preset);
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - p.days);
    return { start: fmtDate(start), end: fmtDate(end) };
  }, [preset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, mc] = await Promise.all([
        api.get(`/reports/overview?start=${range.start}&end=${range.end}`),
        api.get("/reports/missing-cost"),
      ]);
      setData(ov.data);
      setMissing(mc.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load reports (owner only)");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const applyBulkCost = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return toast.error("Tick the items that came from the pallet first");
    if (!palletCost || parseFloat(palletCost) <= 0) return toast.error("Enter the pallet's total cost");
    try {
      const { data: r } = await api.post("/reports/bulk-cost", { product_ids: ids, total_cost: parseFloat(palletCost) });
      toast.success(`Cost set: ${fmtMoney(r.per_item_cost)} each across ${r.updated} items`);
      setSelected({}); setPalletCost("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const saveCost = async (id) => {
    const v = parseFloat(costEdits[id]);
    if (isNaN(v) || v < 0) return toast.error("Enter a valid cost");
    try {
      await api.patch(`/products/admin/${id}`, { cost: v });
      toast.success("Cost saved");
      setCostEdits((c) => ({ ...c, [id]: undefined }));
      load();
    } catch { toast.error("Save failed"); }
  };

  const maxDay = data ? Math.max(...(data.by_day.map((d) => d.revenue)), 1) : 1;

  return (
    <div data-testid="admin-reports">
      <div className="ipv-eyebrow text-[#0A1C40]">Owner only</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mt-2">Reports</h1>

      <div className="flex gap-1 mt-5">
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => setPreset(p.id)}
            className={`px-4 py-1.5 text-sm font-bold rounded-full border ${preset === p.id ? "bg-[#0A1C40] text-white border-[#0A1C40]" : "border-gray-300 text-gray-600 hover:border-gray-500"}`}
            data-testid={`reports-preset-${p.id}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading && <div className="mt-8 text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Crunching numbers…</div>}

      {!loading && data && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Stat label="Revenue" value={fmtMoney(data.revenue)} sub={`${data.orders} orders · AOV ${fmtMoney(data.aov)}`} testid="stat-revenue" />
            <Stat label="Margin" value={data.margin.blended_pct != null ? `${data.margin.blended_pct}%` : "—"}
              sub={`${fmtMoney(data.margin.dollars)} on ${fmtMoney(data.margin.covered_revenue)} costed revenue`} testid="stat-margin" />
            <Stat label="Cost coverage" value={`${data.margin.coverage_pct || 0}%`}
              sub={`${fmtMoney(data.margin.uncovered_revenue)} of revenue has no cost entered`} testid="stat-coverage" />
            <Stat label="Pallet revenue" value={fmtMoney(data.pallets.revenue)} sub={`${data.pallets.count} pallets sold (${data.pallets.custom_pallets} custom-built)`} testid="stat-pallets" />
          </div>

          {data.missing_cost_count > 0 && (
            <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-3 text-sm flex items-center gap-2" data-testid="missing-cost-banner">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span><b>{data.missing_cost_count} active products have no cost entered</b> — they're invisible to your margin picture. Fill them in below.</span>
            </div>
          )}

          {/* Daily revenue bars */}
          <div className="bg-white border border-gray-200 p-5 mt-6" data-testid="daily-chart">
            <div className="ipv-eyebrow mb-4">Revenue by day</div>
            <div className="flex items-end gap-[3px] h-36 overflow-x-auto">
              {data.by_day.map((d) => (
                <div key={d.date} className="flex-1 min-w-[8px] group relative">
                  <div className="bg-[#0A1C40] hover:bg-[#FFC220] transition-colors" style={{ height: `${Math.max(4, (d.revenue / maxDay) * 130)}px` }} title={`${d.date}: ${fmtMoney(d.revenue)} (${d.orders} orders)`} />
                </div>
              ))}
              {data.by_day.length === 0 && <div className="text-xs text-gray-400">No paid orders in this range.</div>}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            {/* Channels + staff */}
            <div className="bg-white border border-gray-200 p-5">
              <div className="ipv-eyebrow mb-3 flex items-center gap-1"><Store className="w-3.5 h-3.5" /> Channels</div>
              <div className="space-y-1.5 text-sm" data-testid="channel-split">
                {Object.entries(data.channels).map(([ch, v]) => (
                  <div key={ch} className="flex justify-between">
                    <span className="uppercase font-bold text-xs tracking-wider">{ch}</span>
                    <span>{fmtMoney(v.revenue)} · {v.orders} orders</span>
                  </div>
                ))}
              </div>
              <div className="ipv-eyebrow mt-6 mb-3">Staff sales</div>
              <div className="space-y-1.5 text-sm" data-testid="staff-leaderboard">
                {data.staff.length === 0 && <div className="text-xs text-gray-400">No staff-attributed sales in range.</div>}
                {data.staff.map((s) => (
                  <div key={s.name} className="flex justify-between">
                    <span>{s.name}</span><span className="font-bold">{fmtMoney(s.revenue)} · {s.orders}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories with margin */}
            <div className="bg-white border border-gray-200 p-5">
              <div className="ipv-eyebrow mb-3 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Category performance</div>
              <table className="w-full text-sm" data-testid="category-table">
                <thead className="text-left ipv-eyebrow"><tr><th className="pb-2">Category</th><th className="pb-2 text-right">Revenue</th><th className="pb-2 text-right">Margin %</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {data.categories.map((c) => (
                    <tr key={c.category}>
                      <td className="py-1.5">{c.category}</td>
                      <td className="py-1.5 text-right font-bold">{fmtMoney(c.revenue)}</td>
                      <td className="py-1.5 text-right">{c.margin_pct != null ? `${c.margin_pct}%` : <span className="text-gray-400">no cost</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top products */}
          <div className="bg-white border border-gray-200 p-5 mt-6">
            <div className="ipv-eyebrow mb-3 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Top products</div>
            <table className="w-full text-sm" data-testid="top-products">
              <thead className="text-left ipv-eyebrow"><tr><th className="pb-2">Product</th><th className="pb-2 text-right">Qty</th><th className="pb-2 text-right">Revenue</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {data.top_products.map((p) => (
                  <tr key={p.product_id}><td className="py-1.5">{p.name}</td><td className="py-1.5 text-right">{p.qty}</td><td className="py-1.5 text-right font-bold">{fmtMoney(p.revenue)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Missing cost + pallet cost spreader */}
          <div className="bg-white border border-gray-200 p-5 mt-6" data-testid="missing-cost-section">
            <div className="ipv-eyebrow mb-1 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Products missing cost ({missing.length})</div>
            <p className="text-xs text-gray-500 mb-4">
              Enter cost per item, or — for pallet-sourced items — tick everything from one pallet, enter the <b>pallet's total cost</b>, and it splits evenly (override any item after).
            </p>
            {missing.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-4 bg-[#FFF8E1] border border-[#FFC220] p-3">
                <span className="text-sm font-bold">{Object.values(selected).filter(Boolean).length} selected ·</span>
                <input type="number" step="0.01" placeholder="Pallet total cost $" className="input-base max-w-[180px]" value={palletCost}
                  onChange={(e) => setPalletCost(e.target.value)} data-testid="pallet-cost-input" />
                <button onClick={applyBulkCost} className="btn-primary text-sm" data-testid="apply-bulk-cost">Split cost evenly</button>
              </div>
            )}
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left ipv-eyebrow sticky top-0 bg-white"><tr><th className="pb-2 w-8"></th><th className="pb-2">Product</th><th className="pb-2">Category</th><th className="pb-2 text-right">Price</th><th className="pb-2 text-right w-44">Cost</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {missing.map((p) => (
                    <tr key={p.id} data-testid={`missing-cost-row-${p.id}`}>
                      <td className="py-1.5"><input type="checkbox" checked={!!selected[p.id]} onChange={(e) => setSelected((s) => ({ ...s, [p.id]: e.target.checked }))} data-testid={`select-${p.id}`} /></td>
                      <td className="py-1.5">{p.name}{p.sku && <span className="text-gray-400 text-xs"> · {p.sku}</span>}</td>
                      <td className="py-1.5 text-xs text-gray-500">{p.category}</td>
                      <td className="py-1.5 text-right">{fmtMoney(p.price)}</td>
                      <td className="py-1.5 text-right">
                        <div className="inline-flex gap-1">
                          <input type="number" step="0.01" placeholder="0.00" className="border border-gray-300 px-2 py-0.5 w-20 text-right text-xs"
                            value={costEdits[p.id] ?? ""} onChange={(e) => setCostEdits((c) => ({ ...c, [p.id]: e.target.value }))} data-testid={`cost-input-${p.id}`} />
                          <button onClick={() => saveCost(p.id)} className="text-xs bg-[#0A1C40] text-white px-2 font-bold" data-testid={`cost-save-${p.id}`}>Save</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {missing.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-gray-400 text-sm">Every active product has a cost — full margin visibility 🎉</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
